import { useEffect, useMemo, useState } from 'react';
import { CustomSelect, FullPageLoader } from '@components';
import FaIcon from '@components/FaIcon';
import MDBox from '@components/MDBox';
import MDButton from '@components/MDButton';
import MDTypography from '@components/MDTypography';
import {
  CircularProgress,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  Switch,
  Tooltip
} from '@mui/material';

import { AvailableRadioOptions, CombineDataType } from '@DStypes/project';
import { convertObjectToArrayOfObjects, formatTitle } from '@utils/common';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@store/store';
import {
  useCombineDataFilesMutation,
  useLazyGetCombineFilesDetailsQuery
} from '@api/projects';
import theme from '@theme/index';

import CombineReviewTable from './CombineReviewTable';
import { MSG_EVENT, MSG_TYPE, NotificationData } from '@DStypes/notifications';
import dayjs from 'dayjs';
import { setUserNotifications } from '@components/Notification/slice';
import { sendAmplitudeData } from '@utils/amplitude';
import { CLICKED_COMBINE_DATA } from '@constants/eventNames';
import CleanActionsModal from '@components/CleanActionsModal';
import ReportGenerationModal from '@pages/ProjectBoard/components/ReportGenerationModal';

interface ICombineFilesContentProps {
  combineData?: CombineDataType;
  setInitiatePolling: React.Dispatch<React.SetStateAction<boolean>>;
  handleCancelCombine: () => void;
  isCancelSuccess: boolean;
}

const AddRowColumnValueString: Record<AvailableRadioOptions, string> = {
  [AvailableRadioOptions.ADD]: 'only add',
  [AvailableRadioOptions.UPDATE]: 'only update',
  [AvailableRadioOptions.BOTH]: 'both',
  [AvailableRadioOptions.NONE]: ''
};

const AvailableColRadioOptionsText: Record<AvailableRadioOptions, string> = {
  [AvailableRadioOptions.ADD]: 'Only add new columns',
  [AvailableRadioOptions.UPDATE]: 'Only update existing columns',
  [AvailableRadioOptions.BOTH]: 'Both',
  [AvailableRadioOptions.NONE]: ''
};

const AvailableColRadioOptionsTooltipText: Record<
  AvailableRadioOptions,
  string
> = {
  [AvailableRadioOptions.ADD]:
    'Choose this to ONLY add new columns, such as Age, Gender, and Preferences.',
  [AvailableRadioOptions.UPDATE]:
    'Choose this if you want to update existing columns with new data. Eg: If you existing file already has Age, Gender, Preferences and you want to update',
  [AvailableRadioOptions.BOTH]:
    'Choose this if you want to both add new columns and update existing ones.',
  [AvailableRadioOptions.NONE]: ''
};

const AvailableRowRadioOptionsTooltipText: Record<
  AvailableRadioOptions,
  string
> = {
  [AvailableRadioOptions.ADD]:
    'Choose this to ONLY add new rows from the new file.',
  [AvailableRadioOptions.UPDATE]:
    'Choose this if you want to update existing rows with new data. Eg: Update marketing campaign called “Project Cat” data like No. of clicks, No. of Impressions, Cost etc. rather than add data from new file as new rows.',
  [AvailableRadioOptions.BOTH]:
    'Select this if you want to both add and update rows.',
  [AvailableRadioOptions.NONE]: ''
};

const AvailableRowRadioOptionsText: Record<AvailableRadioOptions, string> = {
  [AvailableRadioOptions.ADD]: 'Only add new rows',
  [AvailableRadioOptions.UPDATE]: 'Only update existing rows',
  [AvailableRadioOptions.BOTH]: 'Both',
  [AvailableRadioOptions.NONE]: ''
};

export interface CombineDataPayload extends CombineDataType {
  project_id?: string;
  source_datafile_id?: string;
  target_datafile_id?: string;
}
const availableRadioOptions = [
  AvailableRadioOptions.ADD,
  AvailableRadioOptions.UPDATE,
  AvailableRadioOptions.BOTH
];

const CombineFilesContent: React.FC<ICombineFilesContentProps> = ({
  combineData,
  setInitiatePolling,
  handleCancelCombine
}) => {
  const dispatch = useDispatch();
  const projectBoard = useSelector((state: RootState) => state?.projectBoard);
  const [combineDataFiles, { isSuccess: isCombineSuccess }] =
    useCombineDataFilesMutation();

  const [currentSection, setCurrentSection] = useState<number>(1);
  const [isReviewPage, setIsReviewPage] = useState<boolean>(false);
  const [error, setError] = useState({
    sourceFileError: false,
    mainFileError: false
  });
  const [rowColumnError, setRowColumnError] = useState(false);

  const [combineDataPayload, setCombineDataPayload] = useState<
    CombineDataPayload | undefined
  >();

  const [openCleanActionsModal, setOpenCleanActionsModal] =
    useState<boolean>(false);

  const [cleanedColumnsRedirect, setCleanedColumnsRedirect] =
    useState<boolean>(false);
  const [openDownloadModal, setOpenDownloadModal] = useState<boolean>(false);

  const isAllowDuplicatesEnabled = (): boolean => {
    const disableOptionArray = [
      AvailableRadioOptions.UPDATE,
      AvailableRadioOptions.BOTH
    ];

    if (combineDataPayload?.duplicate_rows_frozen) return true;

    if (
      (combineDataPayload as CombineDataPayload)?.add_columns ===
        AvailableRadioOptions.NONE &&
      (combineDataPayload as CombineDataPayload)?.add_rows ===
        AvailableRadioOptions.ADD
    )
      return true;

    if (
      disableOptionArray.includes(
        (combineDataPayload as CombineDataPayload)?.add_columns
      ) ||
      disableOptionArray.includes(
        (combineDataPayload as CombineDataPayload)?.add_rows
      )
    )
      return false;

    if (combineDataPayload?.add_columns === AvailableRadioOptions.ADD)
      return true;

    return false;
  };

  const isDisabledRadioButton = (
    type: AvailableRadioOptions,
    isAddColumn?: boolean
  ) => {
    if (isAddColumn && combineDataPayload?.add_columns_frozen) return true;

    const isDisabled =
      [AvailableRadioOptions.UPDATE, AvailableRadioOptions.BOTH].includes(
        type
      ) && Boolean(combineDataPayload?.duplicate_rows_for_key_field);

    return isDisabled;
  };

  const sendNotification = (msg_event: MSG_EVENT, message: string) => {
    const payload: NotificationData = {
      created_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      is_read: false,
      is_silent: false,
      is_user_message: true,
      message_event: msg_event,
      message_text: message,
      updated_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      message_type: MSG_TYPE.INFO,
      message_data: JSON.stringify({
        project: {
          id: projectBoard?.currentProjectData?.id,
          name: projectBoard?.currentProjectData?.project_name
        }
      }),
      linkKeyword: 'here.',
      replaceKeyword: '<click here>',
      is_important: false,
      recipient_obj: 0,
      isAnchorElement: true
    };

    dispatch(setUserNotifications(payload));
  };

  // Hook to fetch project for combine or combine cancel success
  useEffect(() => {
    if (isCombineSuccess) setInitiatePolling(true);
  }, [isCombineSuccess]);

  const [getCombineDataDetails, { data }] = useLazyGetCombineFilesDetailsQuery(
    {}
  );

  // hook to initiate combine data payload
  useEffect(() => {
    const dataState = data || combineData;

    if (dataState?.id)
      setCombineDataPayload({
        ...dataState,
        source_datafile_id: dataState?.source_datafile.id,
        target_datafile_id: dataState?.target_datafile.id,
        project_id: projectBoard?.currentProjectData?.id,
        initial_target_file_id: projectBoard?.currentProjectData
          ?.project_datafiles?.[0]?.id as string
      });
  }, [combineData, data]);

  useEffect(() => {
    if (combineDataPayload?.selected_source_keyfield)
      setError((prev) => ({ ...prev, sourceFileError: false }));

    if (combineDataPayload?.selected_target_keyfield)
      setError((prev) => ({ ...prev, mainFileError: false }));
  }, [
    combineDataPayload?.selected_source_keyfield,
    combineDataPayload?.selected_target_keyfield
  ]);

  useEffect(() => {
    if (currentSection === 2 && combineDataPayload?.add_columns_frozen)
      incrementSection();
  }, [currentSection]);

  const incrementSection = () => setCurrentSection((prev) => prev + 1);

  const handleErrorValidation = () => {
    if (
      combineDataPayload?.selected_target_keyfield &&
      combineDataPayload?.selected_source_keyfield
    ) {
      incrementSection();

      return;
    }

    if (!combineDataPayload?.selected_target_keyfield)
      setError((prev) => ({ ...prev, mainFileError: true }));

    if (!combineDataPayload?.selected_source_keyfield)
      setError((prev) => ({ ...prev, sourceFileError: true }));
  };

  const handleCombineData = () => {
    if (combineDataPayload?.add_columns !== AvailableRadioOptions.NONE) {
      setIsReviewPage(true);
    } else if (combineDataPayload?.id) {
      sendNotification(
        MSG_EVENT.FILE_COMBINE_STARTED,
        'Your files are being combined. Once complete,\n1. A clean check will be run on your data again\n2. Existing charts on your Dashboard will be updated\n3. You can download combined cleaned data from under Download\nYou will be notified once it is complete!'
      );
      combineDataFiles(combineDataPayload);
    }
  };

  const mainFileOptions = convertObjectToArrayOfObjects(
    combineData?.target_file_keyfields || {}
  );

  const sourceFileOptions = convertObjectToArrayOfObjects(
    combineData?.source_file_keyfields || {}
  );

  const DefaultStep = () => (
    <MDBox display='flex' marginTop={'5px'}>
      <MDTypography
        variant='h5'
        color='info'
        width='max-content'
        fontSize='1.25rem'
        sx={{ whiteSpace: 'nowrap' }}
      >
        Your
      </MDTypography>
      {combineDataPayload?.id ? (
        <>
          <MDBox
            display='flex'
            flexDirection='column'
            alignItems='center'
            width='max-content'
            px='0.5rem'
          >
            <Tooltip
              title={
                combineData?.source_datafile?.filename &&
                combineData?.source_datafile?.filename.length <= 36
                  ? ''
                  : combineData?.source_datafile?.filename || ''
              }
              placement='top'
            >
              <MDTypography
                variant='h5'
                color='secondary'
                textGradient
                width='max-content'
                height='2rem'
                fontSize='1.25rem'
              >
                <>
                  {formatTitle(
                    combineData?.source_datafile?.filename || '',
                    36
                  )}
                </>
              </MDTypography>
            </Tooltip>
            <MDTypography
              variant='h5'
              color='info'
              width='max-content'
              fontSize='1rem'
            >
              (new file)
            </MDTypography>
          </MDBox>
          <MDBox display='flex' flexDirection='column' alignItems='center'>
            <MDTypography
              variant='sm'
              color='info'
              width='max-content'
              fontSize='1.25rem'
              paddingTop='2px'
            >
              data will be merged into
            </MDTypography>
          </MDBox>
          <MDBox
            display='flex'
            flexDirection='column'
            alignItems='center'
            width='max-content'
            px='0.5rem'
          >
            <Tooltip
              title={
                combineData?.target_datafile?.filename &&
                combineData?.target_datafile?.filename.length <= 36
                  ? ''
                  : combineData?.target_datafile?.filename || ''
              }
              placement='top'
            >
              <MDTypography
                variant='h5'
                color='secondary'
                textGradient
                width='max-content'
                height='2rem'
                fontSize='1.25rem'
              >
                {formatTitle(combineData?.target_datafile?.filename || '', 36)}
              </MDTypography>
            </Tooltip>
            <MDTypography
              variant='h5'
              color='info'
              width='max-content'
              fontSize='1rem'
            >
              (existing file)
            </MDTypography>
          </MDBox>
          <MDBox display='flex' flexDirection='column' alignItems='center'>
            <MDTypography
              variant='sm'
              color='info'
              width='max-content'
              fontSize='1.25rem'
              paddingTop='2px'
            >
              data
            </MDTypography>
          </MDBox>
        </>
      ) : (
        <>
          <CircularProgress
            color='secondary'
            size={25}
            sx={{
              marginLeft: 1,
              marginTop: 1
            }}
          />
        </>
      )}
    </MDBox>
  );

  const KeySelection = useMemo(
    () => (
      <MDBox
        marginTop={3}
        sx={{
          opacity: !combineDataPayload || currentSection < 1 ? '0.5' : '1',
          pointerEvents:
            !combineDataPayload || currentSection < 1 ? 'none' : 'all'
        }}
      >
        <Grid
          display='flex'
          justifyContent='space-between'
          px={2}
          alignItems='center'
        >
          <MDBox width='100%'>
            <MDBox display='flex' alignItems='center'>
              <MDTypography variant='h5' color='info' mb={2} fontSize='1.25rem'>
                Select the key column in both files
              </MDTypography>
              <Tooltip
                title={
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      textAlign: 'left'
                    }}
                  >
                    <div>
                      Select key column field that is common to both files, such
                      as User ID, email address, Name, etc. This “unique
                      identifier” will be used to combine your data. It&apos;s
                      okay if the column names are different.
                    </div>
                    <div>
                      <span
                        style={{
                          fontWeight: 'bold',
                          textDecoration: 'underline'
                        }}
                      >
                        Example
                      </span>
                      : If one file uses User ID and the other uses User Name
                      for similar data, select both as the key column field.
                    </div>
                  </div>
                }
                placement='top'
              >
                <MDBox display={{ xs: 'none', lg: 'block' }} sx={{ mb: 2 }}>
                  <FaIcon
                    icon='circle-question'
                    size='sm'
                    style={{
                      verticalAlign: 'middle',
                      marginTop: '-3px',
                      marginLeft: '6px'
                    }}
                    typographyProps={{
                      color: 'info'
                    }}
                  />
                </MDBox>
              </Tooltip>
            </MDBox>

            <MDBox
              display='flex'
              width='100%'
              justifyContent='center'
              columnGap={2}
            >
              <MDBox display='flex' flexDirection='column'>
                <Tooltip
                  title={
                    combineData?.source_datafile?.filename &&
                    combineData?.source_datafile?.filename.length <= 36
                      ? ''
                      : combineData?.source_datafile?.filename || ''
                  }
                  placement='top'
                >
                  <MDTypography
                    variant='h5'
                    color='info'
                    mb={1}
                    fontSize='1rem'
                  >
                    {formatTitle(
                      combineData?.source_datafile?.filename || '',
                      36
                    ) || 'New File'}
                  </MDTypography>
                </Tooltip>
                <CustomSelect
                  placeholder='Select Key Column'
                  color='white'
                  options={sourceFileOptions}
                  value={combineDataPayload?.selected_source_keyfield || ''}
                  disabledTooltipText='This chart type is not available for this kind of data'
                  sx={{
                    minWidth: '300px'
                  }}
                  handleValueChange={(e: any) => {
                    if (combineDataPayload) {
                      setCurrentSection(1);
                      setCombineDataPayload({
                        ...combineDataPayload,
                        selected_source_keyfield: e.target.value
                      });
                      getCombineDataDetails({
                        id: combineDataPayload.source_datafile.id,
                        source_keyfield: e.target.value,
                        target_keyfield:
                          combineDataPayload.selected_target_keyfield
                      });
                    }
                  }}
                />
                <MDTypography variant='caption' color='error' mt={1}>
                  {error?.sourceFileError ? 'Please select a column' : ''}
                </MDTypography>
              </MDBox>

              <Divider
                variant={'fullWidth'}
                sx={{ opacity: 1, height: '70px' }}
                orientation='vertical'
              />

              <MDBox display='flex' flexDirection='column'>
                <Tooltip
                  title={
                    combineData?.target_datafile?.filename &&
                    combineData?.target_datafile?.filename.length <= 36
                      ? ''
                      : combineData?.target_datafile?.filename || ''
                  }
                  placement='top'
                >
                  <MDTypography
                    variant='h5'
                    color='info'
                    mb={1}
                    fontSize='1rem'
                  >
                    {formatTitle(
                      combineData?.target_datafile?.filename || '',
                      36
                    ) || 'Existing File'}
                  </MDTypography>
                </Tooltip>
                <CustomSelect
                  placeholder='Select Key Column'
                  color='white'
                  options={mainFileOptions}
                  value={combineDataPayload?.selected_target_keyfield || ''}
                  disabledTooltipText='This chart type is not available for this kind of data'
                  sx={{
                    minWidth: '300px'
                  }}
                  handleValueChange={(e: any) => {
                    if (combineDataPayload) {
                      setCurrentSection(1);
                      setCombineDataPayload({
                        ...combineDataPayload,
                        selected_target_keyfield: e.target.value
                      });
                      getCombineDataDetails({
                        id: combineDataPayload.source_datafile.id,
                        source_keyfield:
                          combineDataPayload.selected_source_keyfield,
                        target_keyfield: e.target.value
                      });
                    }
                  }}
                />
                <MDTypography variant='caption' color='error' mt={1}>
                  {error?.mainFileError ? 'Please select a column' : ''}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
          <MDBox display={'flex'}>
            <MDBox style={{ marginRight: '10px', marginTop: '3px' }}>
              {currentSection >= 2 && (
                <FaIcon icon='check' style={{ color: '#169e34' }} />
              )}
            </MDBox>
            <Tooltip
              title={'Select/change key column field first and then click OK'}
              placement='top'
            >
              <MDButton
                onClick={() => {
                  if (combineDataPayload && currentSection === 1)
                    handleErrorValidation();
                }}
                color='primary'
                variant='gradient'
                circular={false}
                sx={{
                  marginBottom: '8px',
                  px: '16px',
                  height: '1.5rem',
                  alignSelf: 'flex-end'
                }}
              >
                Ok
              </MDButton>
            </Tooltip>
          </MDBox>
        </Grid>
        <MDTypography
          variant='subtitle'
          sx={{ marginBottom: '-14px', paddingLeft: '16px' }}
        >
          1/3
        </MDTypography>
      </MDBox>
    ),
    [combineDataPayload, currentSection]
  );

  const AddColumns = () => (
    <MDBox
      sx={{
        opacity: !combineDataPayload || currentSection < 2 ? '0.5' : '1',
        pointerEvents:
          !combineDataPayload || currentSection < 2 ? 'none' : 'all'
      }}
    >
      <Grid
        display='flex'
        justifyContent='space-between'
        px={2}
        alignItems='center'
        height='fit-content'
      >
        <MDBox display='flex' alignItems='center'>
          <MDBox display='flex' flexDirection='column'>
            <MDBox display='flex' alignItems='center' columnGap={1}>
              <Tooltip
                title={
                  combineDataPayload?.add_columns_frozen
                    ? 'DataSquirrel did not find any additional columns to add/update in your new file'
                    : ''
                }
                placement='top'
              >
                <div>
                  <Switch
                    checked={
                      combineDataPayload?.add_columns !==
                      AvailableRadioOptions.NONE
                    }
                    color='default'
                    disabled={combineDataPayload?.add_columns_frozen}
                    onChange={(e, value) => {
                      if (combineDataPayload?.add_columns_frozen) {
                        return;
                      } else if (combineDataPayload) {
                        setCurrentSection(2);
                        setRowColumnError(false);
                        setCombineDataPayload({
                          ...combineDataPayload,
                          add_columns: value
                            ? AvailableRadioOptions.ADD
                            : AvailableRadioOptions.NONE
                        });
                      }
                    }}
                  />
                </div>
              </Tooltip>
              <MDTypography
                variant='h5'
                color='info'
                fontSize='1.25rem'
                sx={{
                  opacity: combineDataPayload?.add_columns_frozen ? 0.5 : 1
                }}
              >
                Add Columns
              </MDTypography>
              <Tooltip
                title={
                  combineDataPayload?.add_columns_frozen ? (
                    ''
                  ) : (
                    <div style={{ textAlign: 'left' }}>
                      <div>
                        Select this option if you want to add columns from new
                        to existing file
                      </div>
                      <div>
                        <span
                          style={{
                            fontWeight: 'bold',
                            textDecoration: 'underline'
                          }}
                        >
                          Example
                        </span>
                        : To combine existing Transaction data with new User
                        data, select ON to add columns like Age, Gender, and
                        Preferences from the new file to the existing file.
                      </div>
                    </div>
                  )
                }
                placement='right'
              >
                <MDBox>
                  <FaIcon
                    icon='circle-question'
                    size='sm'
                    style={{
                      verticalAlign: 'middle',
                      marginTop: '-4px',
                      opacity: combineDataPayload?.add_columns_frozen ? 0.5 : 1
                    }}
                    typographyProps={{
                      color: 'info'
                    }}
                  />
                </MDBox>
              </Tooltip>
            </MDBox>
            {!combineDataPayload?.add_columns_frozen &&
            combineDataPayload?.add_columns !== AvailableRadioOptions.NONE ? (
              availableRadioOptions.map((type) => (
                <Grid
                  item
                  display='flex'
                  flexDirection='row'
                  key={type}
                  sx={{
                    opacity: isDisabledRadioButton(type, true) ? 0.5 : 1,
                    pointerEvents:
                      !combineDataPayload || currentSection < 2
                        ? 'none'
                        : 'all',
                    ml: 3
                  }}
                >
                  <Tooltip
                    title={
                      isDisabledRadioButton(type, true)
                        ? 'To select this option, switch off “Allow Duplicates” first.'
                        : ''
                    }
                    placement='right'
                  >
                    <FormControlLabel
                      value={theme}
                      control={<Radio color='secondary' />}
                      label={AvailableColRadioOptionsText[type]}
                      disabled={isDisabledRadioButton(type, true)}
                      checked={combineDataPayload?.add_columns === type}
                      onClick={() => {
                        if (
                          combineDataPayload &&
                          !isDisabledRadioButton(type, true)
                        )
                          setCombineDataPayload(() => ({
                            ...combineDataPayload,
                            add_columns: type
                          }));
                      }}
                    />
                  </Tooltip>
                  <Tooltip
                    title={
                      isDisabledRadioButton(type, true)
                        ? ''
                        : AvailableColRadioOptionsTooltipText[type]
                    }
                    placement='right'
                  >
                    <MDBox>
                      <FaIcon
                        icon='circle-question'
                        size='sm'
                        style={{
                          verticalAlign: 'middle'
                        }}
                        typographyProps={{
                          color: 'info'
                        }}
                      />
                    </MDBox>
                  </Tooltip>
                </Grid>
              ))
            ) : (
              <></>
            )}
            {combineDataPayload?.add_columns_frozen && (
              <MDTypography variant='h5'>No columns to add</MDTypography>
            )}
          </MDBox>
        </MDBox>
        <MDBox display={'flex'}>
          <MDBox style={{ marginRight: '10px', marginTop: '3px' }}>
            {currentSection >= 3 && (
              <FaIcon icon='check' style={{ color: '#169e34' }} />
            )}
          </MDBox>
          <Tooltip
            title='Confirm your selection by clicking OK'
            placement='top'
          >
            <MDButton
              onClick={() => {
                if (combineDataPayload && currentSection === 2)
                  incrementSection();
              }}
              color='primary'
              variant='gradient'
              circular={false}
              sx={{
                px: '16px',
                height: '1.5rem'
              }}
            >
              Ok
            </MDButton>
          </Tooltip>
        </MDBox>
      </Grid>
      <MDTypography
        variant='subtitle'
        sx={{ marginBottom: '-14px', paddingLeft: '16px' }}
      >
        2/3
      </MDTypography>
    </MDBox>
  );

  const SelectRow = () => (
    <MDBox>
      <Grid
        display='flex'
        justifyContent='space-between'
        px={2}
        alignItems='center'
      >
        <MDBox
          sx={{
            opacity: !combineDataPayload || currentSection < 3 ? '0.5' : '1',
            pointerEvents:
              !combineDataPayload || currentSection < 3 ? 'none' : 'all'
          }}
        >
          <MDBox display='flex' alignItems='center' columnGap={1} marginTop={2}>
            <Switch
              color='default'
              checked={
                combineDataPayload?.add_rows !== AvailableRadioOptions.NONE
              }
              onChange={(e, value) => {
                if (
                  combineDataPayload?.duplicate_rows_frozen &&
                  combineDataPayload?.duplicate_rows_for_key_field
                ) {
                  return;
                } else if (combineDataPayload) {
                  setCurrentSection(3);
                  setRowColumnError(false);
                  setCombineDataPayload(() => ({
                    ...combineDataPayload,
                    duplicate_rows_for_key_field: value
                      ? combineDataPayload?.duplicate_rows_for_key_field
                      : false,
                    add_rows: value
                      ? AvailableRadioOptions.ADD
                      : AvailableRadioOptions.NONE
                  }));
                }
              }}
            />
            <MDTypography variant='h5' color='info' fontSize='1.25rem'>
              Add Rows
            </MDTypography>
            <Tooltip
              title={
                <div style={{ textAlign: 'left' }}>
                  <div>
                    Select this option if you want to add rows from new to
                    existing file
                  </div>
                  <div>
                    <span
                      style={{
                        fontWeight: 'bold',
                        textDecoration: 'underline'
                      }}
                    >
                      Example
                    </span>
                    : To consolidate data from two different marketing campaigns
                    or combine data from January and February, select ON to add
                    rows from the new file to the existing file
                  </div>
                </div>
              }
              placement='right'
            >
              <MDBox>
                <FaIcon
                  icon='circle-question'
                  size='sm'
                  style={{
                    verticalAlign: 'middle',
                    marginTop: '-5px'
                  }}
                  typographyProps={{
                    color: 'info'
                  }}
                />
              </MDBox>
            </Tooltip>
          </MDBox>
          {combineDataPayload?.add_rows !== AvailableRadioOptions.NONE &&
            availableRadioOptions.map((type) => (
              <Grid
                item
                display='flex'
                flexDirection='row'
                key={type}
                sx={{
                  opacity: isDisabledRadioButton(type) ? 0.5 : 1,
                  ml: 3,
                  pointerEvents:
                    !combineDataPayload || currentSection < 3 ? 'none' : 'all'
                }}
              >
                <Tooltip
                  title={
                    isDisabledRadioButton(type, true)
                      ? 'To select this option, switch off “Allow Duplicates” first.'
                      : ''
                  }
                  placement='right'
                >
                  <FormControlLabel
                    value={theme}
                    control={<Radio color='secondary' />}
                    label={AvailableRowRadioOptionsText[type]}
                    disabled={isDisabledRadioButton(type)}
                    checked={combineDataPayload?.add_rows === type}
                    onClick={() => {
                      if (combineDataPayload && !isDisabledRadioButton(type))
                        setCombineDataPayload(() => ({
                          ...combineDataPayload,
                          add_rows: type
                        }));
                    }}
                  />
                </Tooltip>

                <Tooltip
                  title={
                    isDisabledRadioButton(type)
                      ? ''
                      : AvailableRowRadioOptionsTooltipText[type]
                  }
                  placement='right'
                >
                  <MDBox>
                    <FaIcon
                      icon='circle-question'
                      size='sm'
                      style={{
                        verticalAlign: 'middle'
                      }}
                      typographyProps={{
                        color: 'info'
                      }}
                    />
                  </MDBox>
                </Tooltip>
              </Grid>
            ))}
          <MDBox display='flex' alignItems='center' columnGap={1}>
            <Tooltip
              title={
                isAllowDuplicatesEnabled() &&
                !combineDataPayload?.duplicate_rows_frozen
                  ? ''
                  : combineDataPayload?.duplicate_rows_frozen
                  ? 'You can’t turn this option off as your existing file already has duplicate values in the key column field'
                  : 'This option is only required when adding ONLY new rows.'
              }
              sx={{ pointerEvents: 'all' }}
            >
              <MDBox>
                <Switch
                  color='default'
                  checked={!!combineDataPayload?.duplicate_rows_for_key_field}
                  disabled={!isAllowDuplicatesEnabled()}
                  onChange={(e, value) => {
                    if (combineDataPayload?.duplicate_rows_frozen) {
                      return;
                    } else if (combineDataPayload) {
                      setCurrentSection(3);
                      setRowColumnError(false);
                      setCombineDataPayload({
                        ...combineDataPayload,
                        duplicate_rows_for_key_field: value
                      });
                    }
                  }}
                  sx={{
                    width: '2.4rem',
                    height: '1.15rem',
                    '& .MuiSwitch-thumb': {
                      width: '.9rem',
                      height: '.9rem'
                    }
                  }}
                />
              </MDBox>
            </Tooltip>
            <MDTypography variant='h5' color='info' fontSize='1.25rem'>
              Allow duplicates (if any)
            </MDTypography>
            <Tooltip
              title={
                <div style={{ textAlign: 'left' }}>
                  <div>
                    When adding new rows, the key column field selected can have
                    a value show more than once.
                  </div>
                  <div>
                    Select this option to allow such values to appear more than
                    once in the combined file. Do not select to avoid duplicate
                    values.
                  </div>
                </div>
              }
              placement='top'
            >
              <MDBox>
                <FaIcon
                  icon='circle-question'
                  size='sm'
                  style={{
                    verticalAlign: 'middle'
                  }}
                  typographyProps={{
                    color: 'info'
                  }}
                />
              </MDBox>
            </Tooltip>
          </MDBox>
        </MDBox>
      </Grid>
      <MDBox display='flex' justifyContent={'space-between'}>
        <MDBox display={'flex'} marginTop={'20px'}>
          <MDTypography variant='subtitle' sx={{ paddingLeft: '16px' }}>
            3/3
          </MDTypography>
        </MDBox>
        <MDBox
          display='flex'
          justifyContent={'flex-end'}
          columnGap={1}
          height='100%'
        >
          <MDButton
            size='large'
            color='secondary'
            variant='outlined'
            onClick={handleCancelCombine}
          >
            Cancel
          </MDButton>
          <Tooltip
            title='Confirm all sections by clicking OK, and then click this button'
            placement='top'
          >
            <div>
              <MDButton
                variant='gradient'
                color='primary'
                size='large'
                sx={{
                  opacity:
                    !combineDataPayload || currentSection < 3 ? '0.5' : '1',
                  pointerEvents:
                    !combineDataPayload || currentSection < 3 ? 'none' : 'all'
                }}
                onClick={() => {
                  sendAmplitudeData(CLICKED_COMBINE_DATA, {
                    rows_option_selected:
                      AddRowColumnValueString[
                        combineDataPayload?.add_rows as AvailableRadioOptions
                      ],
                    columns_option_selected:
                      AddRowColumnValueString[
                        combineDataPayload?.add_columns as AvailableRadioOptions
                      ],
                    allow_duplicates: !isAllowDuplicatesEnabled()
                      ? 'disabled'
                      : combineDataPayload?.duplicate_rows_frozen
                      ? 'mandatory on'
                      : combineDataPayload?.duplicate_rows_for_key_field
                      ? 'on'
                      : 'off'
                  });
                  if (
                    combineDataPayload?.add_columns ===
                      AvailableRadioOptions.NONE &&
                    combineDataPayload?.add_rows === AvailableRadioOptions.NONE
                  ) {
                    setRowColumnError(true);

                    return;
                  }
                  if (combineDataPayload && currentSection === 3)
                    handleCombineData();
                }}
              >
                Combine Data
              </MDButton>
            </div>
          </Tooltip>
        </MDBox>
      </MDBox>
    </MDBox>
  );

  const onClickDownloadOrCancel = () => {
    setOpenDownloadModal((value) => !value);
    if (cleanedColumnsRedirect) setCleanedColumnsRedirect(false);
  };

  const handleCleanedDataDownloadOrCancel = () => {
    setCleanedColumnsRedirect((prev) => !prev);
    onClickDownloadOrCancel();
  };

  return (
    <div
      style={{
        paddingLeft: '10px',
        paddingRight: '10px',
        position: 'relative'
      }}
    >
      {!combineDataPayload && (
        <MDBox
          sx={{ position: 'absolute', zIndex: 350, width: '100%', top: '50%' }}
        >
          <FullPageLoader height='max-content' />
        </MDBox>
      )}
      {!isReviewPage && (
        <MDBox marginTop='10px'>
          <DialogTitle
            sx={{ pl: '20px', pb: 0 }}
            display={'flex'}
            flexDirection={'column'}
          >
            {rowColumnError && (
              <MDTypography variant='caption' color='error' fontSize='1rem'>
                Please select “Add Rows” or “Add columns” or both to Combine
                Data.
              </MDTypography>
            )}
            <MDBox display={'flex'} justifyContent={'space-between'}>
              <MDTypography variant='h3' color='secondary' textGradient>
                Combine Data
              </MDTypography>
              {/* <MDBox display={{ xs: 'none', md: 'flex' }}>
                <Tooltip title='View File information' placement='top'>
                  <IconButton
                    onClick={() => {
                      setOpenCleanActionsModal(true);
                      sendAmplitudeData(CLICKED_VIEW_CLEANED_DATA);
                    }}
                    sx={{ mr: '10px' }}
                  >
                    <FaIcon
                      icon='table-layout'
                      typographyProps={{ color: 'dark', variant: 'xl' }}
                    />
                  </IconButton>
                </Tooltip>
              </MDBox> */}
            </MDBox>
          </DialogTitle>
        </MDBox>
      )}
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          px: '20px',
          pt: 0,
          overflow: 'auto'
        }}
      >
        {!isReviewPage ? (
          <>
            <DefaultStep />
            {KeySelection}
            <Divider
              variant={'fullWidth'}
              sx={{ opacity: 1, borderBottom: '1px solid #8080805e' }}
            />
            <AddColumns />
            <Divider
              variant={'fullWidth'}
              sx={{ opacity: 1, borderBottom: '1px solid #8080805e' }}
            />
            <SelectRow />
          </>
        ) : (
          <CombineReviewTable
            fileId={combineDataPayload?.source_datafile?.id as string}
            projectName={
              projectBoard?.currentProjectData?.project_name as string
            }
            combineDataPayload={combineDataPayload}
            setInitiatePolling={setInitiatePolling}
            handleCancelCombine={handleCancelCombine}
          />
        )}
      </DialogContent>

      {openCleanActionsModal && (
        <CleanActionsModal
          open={openCleanActionsModal}
          onClose={() => setOpenCleanActionsModal(false)}
          setDownloadClicked={handleCleanedDataDownloadOrCancel}
        />
      )}
      {openDownloadModal && (
        <ReportGenerationModal
          open={openDownloadModal}
          onClose={onClickDownloadOrCancel}
          disableDownloadReport
          themeData={[]}
          chartConfigs={[]}
          svgGeneratedCharts={[]}
          cleanedColumnsRedirect={cleanedColumnsRedirect}
        />
      )}
    </div>
  );
};

export default CombineFilesContent;
